import { Outlet } from '@tanstack/react-router';
import { Header } from './Header.tsx';
import { TopBar } from './TopBar.tsx';
import { Sidebar } from './Sidebar.tsx';
import { BottomNav } from './BottomNav.tsx';
import { useAuthStore } from '../store/auth.ts';
import { t } from '@/lib/strings';

export function RootLayout(): JSX.Element {
  // TopBar / Sidebar / BottomNav / Header internals already bail when !user, but
  // <main> reserves space for the chrome with md:ms-64 md:pt-16. When the user is
  // signed out (Login) that offset would push content around with nothing beside
  // it — so condition the offsets and the chrome ground on auth state.
  const user = useAuthStore((s) => s.user);
  return (
    <div className={`min-h-screen flex flex-col ${user ? 'bg-[hsl(var(--chrome))]' : ''}`}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:start-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:text-primary-foreground focus:px-3 focus:py-2 focus:text-sm focus:font-medium"
      >
        {t.nav.skipToMain}
      </a>
      <TopBar />
      <Sidebar />
      {/* Fixed inverted-corner masks so the content panel reads as rounded against
          the chrome where the top app bar meets the nav drawer. Painted (not a
          panel border-radius) so the corner stays put as content scrolls. RTL: the
          drawer is on the inline-start (right), so the inner notch sits at
          right-64 and the outer notch at the left edge. */}
      {user && (
        <div
          aria-hidden
          className="hidden md:block fixed top-16 right-64 z-30 size-8 pointer-events-none"
          style={{ background: 'radial-gradient(circle at bottom left, transparent 31px, hsl(var(--chrome)) 32px)' }}
        />
      )}
      {user && (
        <div
          aria-hidden
          className="hidden md:block fixed top-16 left-0 z-30 size-8 pointer-events-none"
          style={{ background: 'radial-gradient(circle at bottom right, transparent 31px, hsl(var(--chrome)) 32px)' }}
        />
      )}
      <Header />
      <main
        id="main-content"
        tabIndex={-1}
        className={`flex-1 ${user ? 'has-bottom-nav md:ms-64 md:pt-16' : ''}`}
      >
        {user ? (
          <div className="min-h-dvh bg-[hsl(var(--background))] md:min-h-[calc(100dvh-4rem)]">
            <Outlet />
          </div>
        ) : (
          <Outlet />
        )}
      </main>
      <BottomNav />
    </div>
  );
}
